import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Share2, MapPin, ArrowRight, Sparkles } from "lucide-react";
import Countdown from "../components/Countdown";
import { API } from "../lib/api";
import { toast } from "sonner";

// 3 rotating hero images — DR palm beach, Cabarete beach at night, kite surfing
const heroImgs = [
  "https://images.unsplash.com/photo-1757197190683-c3d64f91f0de?auto=format&fit=crop&w=2400&q=85",
  "https://customer-assets.emergentagent.com/job_rachsforty/artifacts/5b6ilw38_Image%201%20-%20homepage.jpeg",
  "https://images.unsplash.com/photo-1755628931496-5b08b241567c?auto=format&fit=crop&w=2400&q=85",
];
const photo1 = "https://images.unsplash.com/photo-1757197190683-c3d64f91f0de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHx0cm9waWNhbCUyMGJlYWNoJTIwcmVzb3J0JTIwZG9taW5pY2FuJTIwcmVwdWJsaWN8ZW58MHx8fHwxNzc2NTU2ODc2fDA&ixlib=rb-4.1.0&q=85";
const photo2 = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80";
const photo3 = "https://images.unsplash.com/photo-1506929562872-bb421503ef21?auto=format&fit=crop&w=1200&q=80";
const photo4 = "https://images.unsplash.com/photo-1530177150700-84cd9a3b059b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxwYWxtJTIwbGVhZiUyMHNoYWRvd3xlbnwwfHx8fDE3NzY1NTY4ODd8MA&ixlib=rb-4.1.0&q=85";

const vibeWords = ["Dancing.", "Beaches.", "Sunsets.", "Rum punch.", "Old friends.", "New friends."];

export default function Home() {
  const [vibeIdx, setVibeIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setVibeIdx((i) => (i + 1) % vibeWords.length), 1800);
    return () => clearInterval(t);
  }, []);

  const share = async () => {
    const url = window.location.origin;
    const text = "You're invited to Rach Fest — my 40th in Cabarete, DR, May 13–16, 2027.";
    if (navigator.share) {
      try { await navigator.share({ title: "Rach Fest", text, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Link copied — text it to your partner!");
    }
  };

  return (
    <div className="bg-cream">
      {/* HERO */}
      <section className="relative min-h-[100vh] overflow-hidden" data-testid="hero">
        {/* Rotating background */}
        <div className="absolute inset-0">
          {heroImgs.map((src, i) => (
            <div key={i} className="hero-slide">
              <img src={src} alt="" className="w-full h-full object-cover vintage-filter kenburns" />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-ink/20 via-ink/30 to-ink/60" />
        </div>

        {/* Sway palm decoration */}
        <div className="absolute -bottom-6 -left-10 pointer-events-none hidden md:block palm-sway" aria-hidden>
          <svg width="280" height="280" viewBox="0 0 200 200" className="opacity-60">
            <g fill="#0a1a12">
              <path d="M100 190 L96 80 L104 80 Z" />
              <path d="M100 90 Q 40 70 10 100 Q 40 82 100 95 Z" />
              <path d="M100 90 Q 160 70 190 100 Q 160 82 100 95 Z" />
              <path d="M100 88 Q 80 20 50 10 Q 85 25 100 92 Z" />
              <path d="M100 88 Q 120 20 150 10 Q 115 25 100 92 Z" />
              <path d="M100 90 Q 30 110 20 150 Q 45 110 100 95 Z" />
              <path d="M100 90 Q 170 110 180 150 Q 155 110 100 95 Z" />
            </g>
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-28 md:pt-32 pb-20 min-h-[100vh] flex flex-col justify-center">
          <p className="text-cream/95 tracking-[0.45em] uppercase text-xs md:text-sm font-bold mb-6 pop-in flex items-center gap-3" data-testid="hero-eyebrow" style={{ animationDelay: "0.1s" }}>
            <span className="inline-block w-8 h-[2px] bg-gold" />
            May 13 — 16 · 2027 · Cabarete, D.R.
            <span className="inline-block w-8 h-[2px] bg-gold" />
          </p>
          <h1 className="font-display font-black leading-[0.82] text-[22vw] md:text-[13rem] lg:text-[15rem]" data-testid="hero-title">
            <span className="block text-cream pop-in" style={{ animationDelay: "0.25s" }}>Rach</span>
            <span className="block italic font-normal shimmer-text pop-in -mt-4 md:-mt-8" style={{ animationDelay: "0.45s" }}>Fest</span>
          </h1>

          <div className="mt-6 md:mt-8 max-w-xl pop-in" style={{ animationDelay: "0.7s" }}>
            <p className="font-display italic text-cream text-2xl md:text-4xl font-medium" data-testid="hero-subtitle">
              Celebrating 40 years of <span className="text-gold font-bold" style={{ textShadow: "0 0 30px rgba(241,183,0,0.4)" }}>great choices.</span>
            </p>

            <div className="mt-6 flex items-baseline gap-3 text-cream">
              <span className="text-xs tracking-[0.4em] uppercase font-bold text-gold">Expect:</span>
              <span className="relative inline-block h-8 overflow-hidden w-56" aria-hidden>
                <span
                  key={vibeIdx}
                  className="font-display italic text-gold-light text-2xl font-bold inline-block pop-in"
                  data-testid="vibe-word"
                >
                  {vibeWords[vibeIdx]}
                </span>
              </span>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4 pop-in" style={{ animationDelay: "0.9s" }} data-testid="hero-ctas">
            <Link to="/rsvp" className="btn-primary text-base pulse-gold" data-testid="hero-rsvp-btn">
              RSVP <ArrowRight className="arrow w-4 h-4 ml-2" />
            </Link>
            <Link to="/stay" className="inline-flex items-center justify-center rounded-full border-2 border-cream text-cream px-7 py-3 font-medium tracking-wide hover:bg-cream hover:text-ink transition-all duration-300" data-testid="hero-book-btn">
              Book Your Room
            </Link>
            <a
              href={`${API}/calendar.ics`}
              className="inline-flex items-center gap-2 text-cream/90 hover:text-gold transition-colors text-sm"
              data-testid="hero-calendar-btn"
            >
              <Calendar className="w-4 h-4" /> Add to calendar
            </a>
            <button
              onClick={share}
              className="inline-flex items-center gap-2 text-cream/90 hover:text-gold transition-colors text-sm"
              data-testid="hero-share-btn"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
          </div>

          <div className="mt-12 hidden md:block">
            <div className="inline-flex items-center gap-3 text-cream/70 text-xs tracking-[0.3em] uppercase animate-bounce">
              <span>scroll</span>
              <span className="w-8 h-[1px] bg-cream/50" />
            </div>
          </div>
        </div>
      </section>

      {/* COUNTDOWN */}
      <section className="relative bg-white py-24 md:py-28 border-y border-shell overflow-hidden" data-testid="countdown-section">
        {/* subtle sparkles */}
        {[
          { top: "18%", left: "8%",  size: 14, delay: "0s",   color: "#FACC15" },
          { top: "72%", left: "14%", size: 10, delay: "1.1s", color: "#FF2E88" },
          { top: "32%", left: "22%", size: 8,  delay: "2.3s", color: "#0EA5E9" },
          { top: "82%", left: "30%", size: 12, delay: "0.6s", color: "#FACC15" },
          { top: "22%", left: "45%", size: 7,  delay: "1.8s", color: "#22C55E" },
          { top: "78%", left: "55%", size: 10, delay: "2.6s", color: "#FACC15" },
          { top: "28%", left: "68%", size: 12, delay: "0.3s", color: "#A855F7" },
          { top: "66%", left: "78%", size: 9,  delay: "1.5s", color: "#FACC15" },
          { top: "20%", left: "86%", size: 14, delay: "2.1s", color: "#FF2E88" },
          { top: "80%", left: "92%", size: 10, delay: "0.9s", color: "#0EA5E9" },
        ].map((s, i) => (
          <svg
            key={i}
            className="twinkle absolute pointer-events-none"
            style={{ top: s.top, left: s.left, width: s.size, height: s.size, animationDelay: s.delay }}
            viewBox="0 0 24 24"
            fill={s.color}
            aria-hidden
          >
            <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" />
          </svg>
        ))}
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 relative">
          <p className="text-center text-xs tracking-[0.45em] uppercase text-terracotta mb-5 font-bold flex items-center justify-center gap-3">
            <span className="inline-block w-6 h-[1px] bg-terracotta/60" />
            Sunsets until Rach Fest
            <span className="inline-block w-6 h-[1px] bg-terracotta/60" />
          </p>
          <Countdown />
        </div>
      </section>

      {/* NOTE FROM RACHEL */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="rachel-note">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="absolute -top-10 right-10 w-72 h-72 rounded-full bg-terracotta/10 blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 md:px-10 grid md:grid-cols-5 gap-12 items-start relative">
          <div className="md:col-span-2">
            <p className="text-xs tracking-[0.4em] uppercase text-terracotta font-bold mb-3 flex items-center gap-3">
              <span className="inline-block w-6 h-[2px] bg-gold" />
              A note from Rach
            </p>
            <h2 className="font-display text-5xl md:text-6xl font-black text-ink leading-[0.95]">
              I can't wait.<br />
              <span className="italic text-terracotta">Truly.</span>
            </h2>
          </div>
          <div className="md:col-span-3 text-lg leading-relaxed text-muted_ink space-y-5">
            <p>
              You are the people who made my first forty <em>magic</em>. My chosen family from every chapter:
              High School, College, Nashville, New York, The Whole World, My Parents and Adopted Parents.
              Some of you haven't met each other yet. That's the part I'm most excited about.
            </p>
            <p>
              Villa Taina is our home base. I've held nearly the whole hotel for us, so meals, drinks, and every
              poolside nap happen here. Events and parties will either be here or at a venue near the hotel.
              38 rooms, first come first serve.
            </p>
            <p>
              If you'd rather post up at another hotel or rental (want your own kitchen, a bigger crew, or your own pool), go for it.
              Where you sleep is up to you.
            </p>
            <p>
              Thank you. I love you. Come Hang.
            </p>
            <p className="font-display italic text-ink text-xl">— Rachel</p>
          </div>
        </div>
      </section>

      {/* PHOTO STRIP */}
      <section className="relative py-12 overflow-hidden bg-sand" data-testid="photo-strip">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 px-2 md:px-4">
          {[photo1, photo2, photo3, photo4].map((p, i) => (
            <div key={i} className="relative aspect-[3/4] overflow-hidden rounded-md group">
              <img
                src={p}
                alt=""
                className="w-full h-full object-cover vintage-filter transition-transform duration-[1200ms] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
          ))}
        </div>
        <p className="text-center text-xs tracking-[0.35em] uppercase text-muted_ink mt-8">
          Cabarete, Dominican Republic
        </p>
      </section>

      {/* QUICK FACTS / CTA */}
      <section className="relative py-24 md:py-32" data-testid="quick-facts">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-3 gap-10 md:gap-6">
            <div className="md:col-span-1">
              <p className="text-xs tracking-[0.35em] uppercase text-terracotta font-medium mb-3">The Fast Facts</p>
              <h3 className="font-display text-4xl md:text-5xl font-black text-ink leading-[1] mb-6">
                Here's what you need to know.
              </h3>
              <Link to="/event" className="btn-ghost" data-testid="home-to-event-btn">The Full Event →</Link>
            </div>
            <div className="md:col-span-2 grid sm:grid-cols-2 gap-5">
              {[
                { t: "When", v: "Thu May 13 – Sun May 16, 2027", c: "bg-terracotta" },
                { t: "Where", v: "Villa Taina · Cabarete", c: "bg-teal" },
                { t: "Fly into", v: "POP (best), STI, or SDQ", c: "bg-sage" },
                { t: "Weather", v: "~85°F · low humidity · island breeze", c: "bg-gold" },
                { t: "Rooms", v: "38 at Villa Taina · first come", c: "bg-purple" },
                { t: "Stay elsewhere?", v: "Totally fine — you're still in", c: "bg-terracotta" },
              ].map((f) => (
                <div key={f.t} className="relative p-6 bg-white border-2 border-shell rounded-lg hover:-translate-y-1 hover:border-terracotta hover:shadow-[0_8px_0_0_#FF2E88] transition-all duration-300">
                  <span className={`absolute top-0 left-6 w-12 h-1.5 ${f.c} rounded-b`} />
                  <p className="font-display italic text-terracotta text-sm mb-2 tracking-wide font-bold uppercase">{f.t}</p>
                  <p className="font-display text-ink text-xl font-black leading-tight">{f.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-gradient-to-br from-terracotta via-purple to-teal text-cream py-28 overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0 grain pointer-events-none opacity-30" />
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-gold/40 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-sage/40 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.45em] uppercase text-gold mb-4 font-bold">Don't overthink it</p>
          <h2 className="font-display text-5xl md:text-8xl font-black leading-[0.92] mb-8 text-white">
            Say you're <span className="italic shimmer-text">in.</span>
          </h2>
          <p className="text-lg text-cream/90 max-w-xl mx-auto mb-10">
            I can't wait for you to meet each other. RSVP first, figure out flights later.
            Rooms at Villa Taina go quick, so if that's your plan — grab one now.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/rsvp" className="bg-gold text-ink hover:bg-gold-light hover:-translate-y-1 transition-all duration-300 rounded-full px-8 py-3.5 font-bold inline-flex items-center gap-2 group shadow-[0_4px_0_0_#CC9A00]" data-testid="final-rsvp-btn">
              RSVP now
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/stay" className="border-2 border-cream rounded-full px-8 py-3 font-bold hover:bg-cream hover:text-terracotta transition-colors" data-testid="final-book-btn">
              Pick a room
            </Link>
          </div>
          <p className="mt-10 text-sm text-cream/70 flex items-center justify-center gap-2">
            <MapPin className="w-4 h-4" /> Cabarete · Dominican Republic · 18.73°N
          </p>
        </div>
      </section>
    </div>
  );
}
